import React, { useEffect, useState, useRef } from 'react';
import { BRAND_TILE_FEED_POSITION } from './constants/banners';
import Classes from './BrandTileBanner.scss';

// Static div ID for the first brand tile (defined in defineSlotsAdapter)
const STATIC_BRAND_TILE_DIV_ID = 'premium-content-brandtile-dt';

type TBrandTileBannerProps = {
  feedIndex: number;
};

/**
 * BrandTileBanner component for brand tile ad positions.
 *
 * Following Google's infinite content pattern:
 * - First brand tile (position 4): Uses static div ID, slot defined via setupAdditionalBanners
 * - Subsequent tiles (positions 44, 84, etc.): Pass container ref to setupAdditionalBanners
 *   which creates the div, defines the slot, and calls display() in the same GPT callback
 *
 * @see https://developers.google.com/publisher-tag/samples/infinite-content
 */
const BrandTileBanner = ({ feedIndex }: TBrandTileBannerProps) => {
  const isFirstBrandTile = feedIndex === BRAND_TILE_FEED_POSITION;
  const [isBannerLibReady, setIsBannerLibReady] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const HOME_BRAND_TILE = 'home_brandtile';

  // Check if banner lib is already ready, or wait for the event
  useEffect(() => {
    if (typeof window.ecgJsBanners?.setupAdditionalBanners === 'function') {
      setIsBannerLibReady(true);
      return;
    }

    const handleBannerLibLoaded = () => {
      setIsBannerLibReady(true);
    };

    window.addEventListener('banner-lib-loaded', handleBannerLibLoaded);

    return () => {
      window.removeEventListener('banner-lib-loaded', handleBannerLibLoaded);
    };
  }, []);

  // Setup and display the first brand tile using setupAdditionalBanners
  useEffect(() => {
    if (!isFirstBrandTile || !isBannerLibReady) {
      return;
    }

    if (!window.ecgJsBanners?.setupAdditionalBanners) {
      return;
    }

    // For the first brand tile, call setupAdditionalBanners after the div is in the DOM
    window.ecgJsBanners.setupAdditionalBanners([HOME_BRAND_TILE]);
  }, [isFirstBrandTile, isBannerLibReady]);

  // Setup infinite banner for subsequent brand tiles
  useEffect(() => {
    if (
      isFirstBrandTile ||
      !isBannerLibReady ||
      !window.ecgJsBanners?.setupAdditionalBanners ||
      !containerRef.current
    ) {
      return;
    }

    window.ecgJsBanners.setupAdditionalBanners([HOME_BRAND_TILE], {
      infiniteSlot: true,
      container: containerRef.current,
    });
  }, [isFirstBrandTile, isBannerLibReady]);

  // For the first brand tile, render a div with the static ID
  // For subsequent tiles, render a container that setupAdditionalBanners will populate
  if (isFirstBrandTile) {
    return (
      <div
        id={STATIC_BRAND_TILE_DIV_ID}
        className={Classes.container}
        data-testid="brand-tile-banner"
        data-feed-index={feedIndex}
      />
    );
  }

  return (
    <div ref={containerRef} className={Classes.container} data-testid="brand-tile-banner" data-feed-index={feedIndex} />
  );
};

export default BrandTileBanner;
